/**
 * Currency Utilities
 * Formatting helpers for currency display
 */

const ILS_FORMATTER = new Intl.NumberFormat('he-IL', {
  style: 'currency',
  currency: 'ILS',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const NUMBER_FORMATTER = new Intl.NumberFormat('he-IL', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const COMPACT_NUMBER_FORMATTER = new Intl.NumberFormat('he-IL', {
  notation: 'compact',
  compactDisplay: 'short',
});

/**
 * Format a number as currency with the symbol on the right (e.g. 17.2$, 17.2₪).
 */
export function formatCurrencyRight(value: number, currency = 'ILS'): string {
  const parts = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).formatToParts(value);
  const symbol = parts.find((p) => p.type === 'currency')?.value ?? currency;
  const number = parts.filter((p) => p.type !== 'currency' && p.type !== 'literal').map((p) => p.value).join('');
  return `${number}${symbol}`;
}

/**
 * Format a number as currency. Defaults to ILS; pass a currency code (e.g. 'USD', 'EUR') to override.
 */
export function formatCurrency(value: number, currency = 'ILS'): string {
  if (currency === 'ILS') return ILS_FORMATTER.format(value);
  // Use en-US locale for non-ILS currencies so the symbol appears on the left
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Format a number with ILS symbol prefix
 */
export function formatCurrencyShort(value: number): string {
  if (value >= 1000000) {
    return `${COMPACT_NUMBER_FORMATTER.format(value)} ILS`;
  }
  return ILS_FORMATTER.format(value);
}

/**
 * Format a number with locale-specific formatting
 */
export function formatNumber(value: number, decimals: number = 2): string {
  return new Intl.NumberFormat('he-IL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format a large number in compact form (e.g., 1.2K, 3.5M)
 */
export function formatCompactNumber(value: number): string {
  return COMPACT_NUMBER_FORMATTER.format(value);
}

/**
 * Format energy in kWh
 */
export function formatEnergy(kwh: number): string {
  if (kwh >= 1000) {
    return `${formatNumber(kwh / 1000, 1)} MWh`;
  }
  return `${formatNumber(kwh, 1)} kWh`;
}

/**
 * Format power in kW
 */
export function formatPower(kw: number): string {
  return `${formatNumber(kw, 1)} kW`;
}

/**
 * Format percentage
 */
export function formatPercentage(value: number, decimals: number = 1): string {
  return `${formatNumber(value, decimals)}%`;
}

/**
 * Format a duration in minutes to human readable string
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${mins}m`;
}

/**
 * Format a date/time for display
 */
export function formatDateTime(date: Date): string {
  return new Intl.DateTimeFormat('he-IL', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

/**
 * Format a time for display
 */
export function formatTime(date: Date): string {
  return new Intl.DateTimeFormat('he-IL', {
    timeStyle: 'short',
  }).format(date);
}

/**
 * Format a date for display
 */
export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('he-IL', {
    dateStyle: 'short',
  }).format(date);
}

/**
 * Format relative time (e.g., "5 minutes ago")
 */
export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'Just now';
  }
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  if (diffDays === 1) {
    return 'Yesterday';
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }
  return formatDate(date);
}
